import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Tab, Tabs, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PageHeader from "../../../app/layout/PageHeader";
import { api } from "../../../api/apiClient";
import { useStaticData } from "../../../context/StaticDataContext";
import { ServiceCategoryGroup } from "../../types/types";
import "../../../css/tables.css";
type Tag = {
  serviceAttributeId: number;
  code: string;
  name: string;
  description?: string;
  isActive: boolean;
};
type GroupForm = Omit<ServiceCategoryGroup, "categoryGroupId" | "categories">;
const blankGroup: GroupForm = {
  code: "",
  name: "",
  description: "",
  icon: "activities",
  displayOrder: 50,
  isActive: true
};
const icons = ["support", "wellbeing", "activities", "opportunities", "food", "mentalHealth", "family", "housing", "money", "youth", "olderPeople", "employment", "volunteering", "people", "calendar", "weight"];
export default function StaticDataManagementPage() {
  const {
    data,
    refresh
  } = useStaticData();
  const [tab, setTab] = useState(0);
  const [tags, setTags] = useState<Tag[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ServiceCategoryGroup | null | undefined>(undefined);
  const [editingTag, setEditingTag] = useState<Tag | null | undefined>(undefined);
  const [groupForm, setGroupForm] = useState<GroupForm>(blankGroup);
  const [tagForm, setTagForm] = useState({
    code: "",
    name: "",
    description: "",
    isActive: true
  });
  const [assignments, setAssignments] = useState<Record<number, number>>({});
  const groups = data?.serviceCategoryGroups ?? [];
  const categories = groups.flatMap(g => g.categories);
  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      setTags(await api<Tag[]>("/api/staticdata/activity-tags"));
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to load reference data.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void loadTags();
  }, [loadTags]);
  const run = async (action: () => Promise<unknown>) => {
    setSaving(true);
    setError("");
    try {
      await action();
      await refresh();
      await loadTags();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save reference data.");
      return false;
    } finally {
      setSaving(false);
    }
  };
  const openGroup = (g: ServiceCategoryGroup | null) => {
    setEditingGroup(g);
    setGroupForm(g ?? blankGroup);
  };
  const openTag = (t: Tag | null) => {
    setEditingTag(t);
    setTagForm(t ? {
      ...t,
      description: t.description ?? ""
    } : {
      code: "",
      name: "",
      description: "",
      isActive: true
    });
  };
  return <Box sx={{
    minWidth: 0
  }}>
  <PageHeader title="Static data" subtitle="Manage service classification and activity tags." actions={tab !== 1 ? <Button startIcon={<AddIcon />} onClick={() => tab === 0 ? openGroup(null) : openTag(null)}>Add {tab === 0 ? "group" : "activity tag"}</Button> : undefined} />
  {error && <Alert severity="error" sx={{
      mb: 2
    }}>{error}</Alert>}
  <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{
      mb: 2
    }}>
   <Tab label="Category groups" /><Tab label="Category assignments" /><Tab label="Activity tags" />
  </Tabs>
  <div className="card" style={{
      overflowX: "auto",
      minWidth: 0
    }}>
   {loading && <p role="status">Loading reference data…</p>}
   {!loading && tab === 0 && <table className="app-data-table"><thead><tr><th>Group</th><th>Display order</th><th>Status</th><th /></tr></thead><tbody>
    {groups.map(g => <tr key={g.categoryGroupId}><td><strong>{g.name}</strong><div>{g.description}</div></td><td>{g.displayOrder}</td><td>{g.isActive ? "Active" : "Inactive"}</td>
     <td><Button startIcon={<EditIcon />} onClick={() => openGroup(g)} aria-label={`Edit ${g.name}`}>Edit</Button></td></tr>)}
   </tbody></table>}
   {!loading && tab === 1 && <table className="app-data-table"><thead><tr><th>Category</th><th>Group</th><th /></tr></thead><tbody>
    {categories.map(c => <tr key={c.serviceCategoryId}><td>{c.name}{c.isActive === false ? " (inactive)" : ""}</td><td>
     <select className="form-input" aria-label={`Group for ${c.name}`} value={assignments[c.serviceCategoryId] ?? c.categoryGroupId} onChange={e => setAssignments({
                ...assignments,
                [c.serviceCategoryId]: Number(e.target.value)
              })}>
      {groups.filter(g => g.isActive || g.categoryGroupId === c.categoryGroupId).map(g => <option key={g.categoryGroupId} value={g.categoryGroupId} disabled={!g.isActive}>{g.name}{!g.isActive ? " (inactive)" : ""}</option>)}
     </select></td><td><Button disabled={saving || !assignments[c.serviceCategoryId] || assignments[c.serviceCategoryId] === c.categoryGroupId} onClick={async () => {
                if (await run(() => api(`/api/staticdata/service-categories/${c.serviceCategoryId}/group`, {
                  method: "PUT",
                  body: {
                    categoryGroupId: assignments[c.serviceCategoryId]
                  },
                  notifySuccess: "Category group updated."
                }))) setAssignments(prev => {
                  const next = {
                    ...prev
                  };
                  delete next[c.serviceCategoryId];
                  return next;
                });
              }}>Save</Button></td></tr>)}
   </tbody></table>}
   {!loading && tab === 2 && <table className="app-data-table"><thead><tr><th>Activity tag</th><th>Status</th><th /></tr></thead><tbody>
    {tags.map(t => <tr key={t.serviceAttributeId}><td>{t.name}</td><td>{t.isActive ? "Active" : "Inactive"}</td><td><Button startIcon={<EditIcon />} onClick={() => openTag(t)}>Edit</Button></td></tr>)}
   </tbody></table>}
   {!loading && tab === 0 && !groups.length && <p>No category groups are available.</p>}
  </div>
  <Dialog open={editingGroup !== undefined} onClose={() => !saving && setEditingGroup(undefined)} fullWidth maxWidth="sm">
   <DialogTitle>{editingGroup ? "Edit" : "Add"} category group</DialogTitle><DialogContent>
    <Stack spacing={2} sx={{
          pt: 1
        }}>
     <TextField label="Code" value={groupForm.code} disabled={!!editingGroup} onChange={e => setGroupForm({
            ...groupForm,
            code: e.target.value.toUpperCase()
          })} inputProps={{
            maxLength: 100
          }} required />
     <TextField label="Name" value={groupForm.name} onChange={e => setGroupForm({
            ...groupForm,
            name: e.target.value
          })} inputProps={{
            maxLength: 200
          }} required />
     <TextField label="Description" multiline minRows={2} value={groupForm.description ?? ""} onChange={e => setGroupForm({
            ...groupForm,
            description: e.target.value
          })} inputProps={{
            maxLength: 500
          }} />
     <TextField label="Icon" select value={groupForm.icon ?? ""} onChange={e => setGroupForm({
            ...groupForm,
            icon: e.target.value
          })}>
      <MenuItem value="">Default</MenuItem>{icons.map(icon => <MenuItem key={icon} value={icon}>{icon}</MenuItem>)}
     </TextField>
     <TextField label="Display order" type="number" value={groupForm.displayOrder} inputProps={{
            min: 0
          }} onChange={e => setGroupForm({
            ...groupForm,
            displayOrder: Number(e.target.value)
          })} />
     <FormControlLabel label="Active" control={<Checkbox checked={groupForm.isActive} onChange={e => setGroupForm({
            ...groupForm,
            isActive: e.target.checked
          })} />} />
    </Stack>
   </DialogContent><DialogActions><Button disabled={saving} onClick={() => setEditingGroup(undefined)}>Cancel</Button>
    <Button variant="contained" disabled={saving || !groupForm.name.trim() || !groupForm.code || groupForm.displayOrder < 0} onClick={async () => {
          const body = {
            ...groupForm,
            icon: groupForm.icon || null
          };
          if (await run(() => api(`/api/staticdata/service-category-groups${editingGroup ? "/" + editingGroup.categoryGroupId : ""}`, {
            method: editingGroup ? "PUT" : "POST",
            body,
            notifySuccess: "Category group saved."
          }))) setEditingGroup(undefined);
        }}>Save</Button>
   </DialogActions>
  </Dialog>
  <Dialog open={editingTag !== undefined} onClose={() => !saving && setEditingTag(undefined)} fullWidth maxWidth="sm">
   <DialogTitle>{editingTag ? "Edit" : "Add"} activity tag</DialogTitle><DialogContent><Stack spacing={2} sx={{
          pt: 1
        }}>
    <TextField label="Code" value={tagForm.code} disabled={!!editingTag} required inputProps={{
            maxLength: 50
          }} onChange={e => setTagForm({
            ...tagForm,
            code: e.target.value.toUpperCase()
          })} />
    <TextField label="Name" value={tagForm.name} required inputProps={{
            maxLength: 100
          }} onChange={e => setTagForm({
            ...tagForm,
            name: e.target.value
          })} />
    <TextField label="Description" value={tagForm.description} inputProps={{
            maxLength: 500
          }} onChange={e => setTagForm({
            ...tagForm,
            description: e.target.value
          })} />
    <FormControlLabel label="Active" control={<Checkbox checked={tagForm.isActive} onChange={e => setTagForm({
            ...tagForm,
            isActive: e.target.checked
          })} />} />
   </Stack></DialogContent><DialogActions><Button disabled={saving} onClick={() => setEditingTag(undefined)}>Cancel</Button>
    <Button variant="contained" disabled={saving || !tagForm.name.trim() || !tagForm.code} onClick={async () => {
          if (await run(() => api(`/api/staticdata/activity-tags${editingTag ? "/" + editingTag.serviceAttributeId : ""}`, {
            method: editingTag ? "PUT" : "POST",
            body: tagForm,
            notifySuccess: "Activity tag saved."
          }))) setEditingTag(undefined);
        }}>Save</Button>
   </DialogActions>
  </Dialog>
 </Box>;
}
